import {Mapper} from '@s-ui/domain'

/**
 * @implements Mapper
 */
export default class TrackingProfessionalStockMapper extends Mapper {
  /**
   * @constructor
   * @param {Object} param
   * @param {Object} param.config
   * @param {Object} param.fromPageTrackingJsonToTrackingDataValueObjectMapper
   * @param {Object} param.fromPageTrackingJsonToAdvertisementsValueObjectMapper
   * @param {Object} param.fromSearchJSONToFiltersTrackingMapper
   */
  constructor({
    config,
    fromPageTrackingJsonToTrackingDataValueObjectMapper,
    fromPageTrackingJsonToAdvertisementsValueObjectMapper,
    fromSearchJSONToFiltersTrackingMapper
  }) {
    super()

    this._config = config
    this._fromPageTrackingJsonToTrackingDataValueObjectMapper = fromPageTrackingJsonToTrackingDataValueObjectMapper
    this._fromPageTrackingJsonToAdvertisementsValueObjectMapper = fromPageTrackingJsonToAdvertisementsValueObjectMapper
    this._fromSearchJSONToFiltersTrackingMapper = fromSearchJSONToFiltersTrackingMapper
  }

  _getProductsFrom({advertisements, listType}) {
    if (!advertisements) return

    const ads = advertisements.map(ad => ({
      ...ad,
      price: 0,
      quantity: 0
    }))

    const {products} = this._fromPageTrackingJsonToAdvertisementsValueObjectMapper
      .setParams({
        adsTypeName: 'ad_impression',
        listType
      })
      .map({
        ads
      })
      .toJSON()

    return products
  }

  _getPageNameOverwritten({culture, tab}) {
    const {TRACKING_TRANSLATIONS_BY_CULTURE, STOCK_TAB_NAME} = this._config.get('tracking')
    const TRANSLATIONS_BY_CULTURE = TRACKING_TRANSLATIONS_BY_CULTURE[culture]
    const isInformationTab = tab === STOCK_TAB_NAME.INFORMATION
    const isDealerInactivePage = Boolean(!tab)

    const pageNameMap = [
      {
        check: () => isInformationTab,
        result: () => TRANSLATIONS_BY_CULTURE.PROFESSIONAL_STOCK_INFO_PAGE_NAME
      },
      {
        check: () => isDealerInactivePage,
        result: () => TRANSLATIONS_BY_CULTURE.PROFESSIONAL_STOCK_INACTIVE_PAGE_NAME
      },
      {
        check: () => true,
        result: () => TRANSLATIONS_BY_CULTURE.PROFESSIONAL_STOCK_PAGE_NAME
      }
    ]

    return pageNameMap.find(({check}) => check()).result()
  }

  /**
   * @param {Object} param
   * @param {String} param.page
   * @param {String} param.culture
   * @param {Object} param.data
   * @param {Array} param.data.advertisements
   * @param {String} param.data.lastFilter
   * @param {Number} param.data.pageNumber
   * @param {String} param.data.province
   * @param {Object} param.data.search
   * @param {Number} param.data.searchResultsCount
   * @param {String} param.data.tab
   * @param {String} param.data.title
   */
  map({culture, data}) {
    const {
      advertisements,
      lastFilter: lastFilterData,
      pageNumber = this._config.get('DEFAULT_PAGE_NUMBER'),
      province = '',
      ratings,
      search,
      searchResultsCount,
      tab,
      title = ''
    } = data
    const {TRACKING_TRANSLATIONS_BY_CULTURE, FILTER_EVENT_NAME, STOCK_TAB_NAME} = this._config.get('tracking')
    const isInformationTab = tab === STOCK_TAB_NAME.INFORMATION
    const isRatingsTab = tab === STOCK_TAB_NAME.RATINGS
    const TRANSLATIONS_BY_CULTURE = TRACKING_TRANSLATIONS_BY_CULTURE[culture]

    const tabKeys = {
      [STOCK_TAB_NAME.INFORMATION]: 'PROFESSIONAL_STOCK_INFO',
      [STOCK_TAB_NAME.RATINGS]: 'PROFESSIONAL_STOCK_RATINGS'
    }

    const tabPage = tabKeys[tab] || 'PROFESSIONAL_STOCK'

    let trackingData = {}

    if (!isRatingsTab) {
      trackingData = {
        page_name: this._getPageNameOverwritten({culture, tab}),
        page_category_level1: TRANSLATIONS_BY_CULTURE.PROFESSIONAL_STOCK_CATEGORY_LEVEL_1,
        ...(province && {page_category_level2: province.toLowerCase()}),
        ...(title && {page_category_level3: title.toLowerCase()})
      }

      const {filters: searchFilters, lastFilter} = this._fromSearchJSONToFiltersTrackingMapper.map({
        search,
        lastFilter: lastFilterData
      })

      if (!isInformationTab) {
        const products = this._getProductsFrom({
          advertisements,
          listType: TRANSLATIONS_BY_CULTURE.PROFESSIONAL_STOCK_TYPE
        })

        trackingData = {
          ...trackingData,
          search_result_number: searchResultsCount,
          list_page_num: pageNumber,
          products
        }
      }

      if (searchFilters) trackingData.filters = searchFilters

      if (lastFilter)
        trackingData = {
          ...trackingData,
          event_name: FILTER_EVENT_NAME,
          last_filter: lastFilter
        }
    }

    if (isRatingsTab) {
      trackingData = {
        hit_information: ratings?.totalReviews || 0,
        rating_value: ratings?.average || 0
      }
    }

    return this._fromPageTrackingJsonToTrackingDataValueObjectMapper
      .setParams({
        page: tabPage,
        culture
      })
      .map(trackingData)
  }
}
